import * as React from 'react';
// @ts-expect-error
import {components} from 'react-select';
// @ts-expect-error
import type {InputProps} from 'react-select';

import {colors} from 'shells/colors/colors';
import {Body} from 'shells/typography/body';

import type {ActivityTypeIcon} from 'nutshell-graphql-types';

import {getIconForActivityType} from '../../setup/activity-types/helpers';

import {useGetSessionUser} from '../use-get-session-user';

import {GraphQLSelect} from './graphql-select';
import {useGetActivityTypes} from './hooks/use-get-activity-types';

type ActivityOption = {
    value: string;
    label: string;
    id: string;
    name: string;
    icon: ActivityTypeIcon;
};

type Props = {
    selectedId: string | null | undefined;
    selectedIds?: string[];
    onChange: (newValue: any) => any;
    onClearValue?: () => void;
    placeholder?: string;
    labelKey?: string;
    valueKey?: string;
    autoFocus?: boolean;
    autoBlur?: boolean;
    isLoading?: boolean;
    noResultsText?: string;
    styles?: any;
    menuPortalTarget?: HTMLElement;
    defaultsToFirstItem?: boolean;
    includeManageOption?: boolean;
    isCreateable?: boolean;
    isValidNewOption?: (inputValue: string) => boolean;
    formatCreateLabel?: (inputValue: string) => string;
    showFooterButton?: boolean;
    showSelectedValueTitle?: boolean;
    minHeight?: number;
};

export const GraphQLSelectActivityTypes = (props: Props) => {
    const {selectedIds, includeManageOption, placeholder, ...restProps} = props;
    const {user} = useGetSessionUser();
    const {activityTypes} = useGetActivityTypes();

    const models = activityTypes
        .filter((model) => !(selectedIds && selectedIds.includes(model.id)))
        .map((model) => ({
            value: model.id,
            label: model.name,
            id: model.id,
            name: model.name,
            icon: model.icon,
        }));

    return (
        <GraphQLSelect
            {...restProps}
            models={models || []}
            // @ts-expect-error
            components={{
                Input: HiddenCursorInput,
            }}
            footerButton={
                includeManageOption && user && user.permissions.canAccessSetup
                    ? {
                          label: 'Manage types',
                          onClick: () => {
                              window.open('/setup/activity-type', '_blank');
                          },
                      }
                    : undefined
            }
            formatOptionLabel={formatOptionLabel}
            placeholder={placeholder || getPlaceholderText(props.isCreateable, models)}
        />
    );
};

// hides cursor until user starts typing, otherwise it looks strange with the icon
const HiddenCursorInput = (
    props: InputProps & {
        onChange?: (e: React.ChangeEvent<any>) => void;
        onBlur?: (e: React.FocusEvent<any>) => void;
    }
) => {
    const [hasInput, setHasInput] = React.useState(false);

    // @ts-expect-error
    const handleChange = (e) => {
        setHasInput(e.target.value.length > 0);
        if (props.onChange) {
            props.onChange(e);
        }
    };

    // @ts-expect-error
    const handleBlur = (e) => {
        setHasInput(false);
        if (props.onBlur) {
            props.onBlur(e);
        }
    };

    return (
        <components.Input
            {...props}
            onChange={handleChange}
            onBlur={handleBlur}
            style={{
                caretColor: hasInput ? 'auto' : 'transparent',
            }}
        />
    );
};

const getPlaceholderText = (
    isCreateable: boolean | null | undefined,
    activityTypeOptions: ActivityOption[]
) => {
    if (isCreateable) {
        return activityTypeOptions.length ? 'Choose an activity…' : 'Create an activity…';
    }

    return 'Activity types…';
};

// @ts-expect-error
const formatOptionLabel = (option) => {
    const Icon = getIconForActivityType(option.icon);

    return (
        <div className='flex align-center gap-8'>
            <Icon
                size={16}
                fill={colors.greyLt}
                color={colors.greyLt}
                style={{color: colors.greyLt}}
                wrapWithDiv={false}
            />
            <Body size='small'>{option.label}</Body>
        </div>
    );
};
